import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import FormHeader from 'components/global/FormHeader';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import fetchUserDetails from 'services/common/fetchUserDetails';
import { ADVANCE_WMS_MODULE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS, { mergePaths } from 'utils/paths';

import { faArrowLeft, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import * as S from './OTOPack.styled';

const OTOPack = () => {
	const { state } = useLocation();

	const schema = useMemo(
		() =>
			yup.object().shape({
				otoPackManualOption: yup.boolean().nullable(),
				otoPackDeactivatePicking: yup.boolean().nullable(),
				pickingMethod: yup.string().nullable(),
				maxWaveSize: yup.number().nullable(),
				activateBins: yup.boolean().nullable(),
				otoPackActivatePackInvCheck: yup.boolean().nullable(),
				otoPackActivateWaveInvCheck: yup.boolean().nullable(),
				inventoryIntegrationType: yup.string().nullable(),
				otoPackPackingPageAutomaticPrintAWB: yup.boolean().nullable(),
				otoPackPackingPageAutomaticShipment: yup.boolean().nullable(),
				activateBundleFeature: yup.boolean().nullable(),
				otoPackUpdateStatusAfterShipping: yup.boolean().nullable()
			}),
		[]
	);
	const axios = useAxiosPrivate();
	const isAuthorized = useAccessControl();
	const formOptions = {
		shouldValidate: true,
		shouldDirty: true
	};

	const [loading, setLoading] = useState(false);

	const { showSuccess } = useAlert();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			otoPackManualOption: null,
			otoPackDeactivatePicking: null,
			pickingMethod: null,
			maxWaveSize: null,
			activateBins: null,
			otoPackActivatePackInvCheck: null,
			otoPackActivateWaveInvCheck: null,
			activateBundleFeature: null,
			inventoryIntegrationType: null,
			otoPackPackingPageAutomaticPrintAWB: null,
			otoPackPackingPageAutomaticShipment: null,
			otoPackUpdateStatusAfterShipping: null
		}
	});
	const {
		handleSubmit,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting },
		watch,
		setValue
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const fillForm = async () => {
		setLoading(true);

		try {
			const response = await axios.post(ADVANCE_WMS_MODULE.GET_CONFIGURE_OTOPACK);
			if (response.status === 200) {
				reset(response.data);
				return;
			}
			AppLogger.error(response);
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	const onSubmit = async (data) => {
		setLoading(true);
		// inventoryIntegrationType must be read-only
		if (data.inventoryIntegrationType) {
			delete data.inventoryIntegrationType;
		}
		if (!data.activateBins) {
			data.otoPackActivatePackInvCheck = false;
			data.otoPackActivateWaveInvCheck = false;
		}

		try {
			const response = await axios.post(ADVANCE_WMS_MODULE.CONFIGURE_OTOPACK, data);
			if (response.status === 200) {
				reset(data);
				fetchUserDetails();
				showSuccess();
				return;
			}
			AppLogger.error(response);
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	const colSpan = {
		lg: 8,
		xl: 7
	};

	useEffect(() => {
		fillForm();
	}, []);

	const activateBins = watch('activateBins');

	useEffect(() => {
		if (activateBins !== null && activateBins) {
			setValue('otoPackDeactivatePicking', false, formOptions);
		}
	}, [activateBins]);

	return (
		<div>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader
					boldTitle="otoPack"
					{...(state?.showBackButton
						? {
								lightTitle: state?.otoModule
									? 'backToOTOWarehouseManagementModule'
									: 'backToOTOPackConfigurationPage',
								icon: faArrowLeft,
								backUrl: state?.otoModule
									? PATHS.WAREHOUSE_MANAGEMENT_MODULE
									: mergePaths(PATHS.WMS_CONFIGURATION, PATHS.WMS_CONFIGURATION_USERS)
							}
						: {})}
				/>
				<S.ButtonContainer>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						icon={faFloppyDisk}
						label="save"
						variant="blue"
						className="saveButton"
					/>
				</S.ButtonContainer>
			</HeaderContainerFormHeader>
			<S.StyledForm formMethods={formMethods} borderless>
				<S.StyledCard className="settingsMaxWidth" fullWidth>
					{isAuthorized('IMSModule') && (
						<>
							<FormItem
								label="inventoryIntegrationType"
								colSpan={colSpan}
								isLoading={loading}
								description="inventoryIntegrationType.description">
								<StringInput name="inventoryIntegrationType" disabled />
							</FormItem>
							<F.Line className="line" />
						</>
					)}

					{isAuthorized(['WMSModule', 'WMSAdvanceModule']) && (
						<FormItem
							label="otoPackManualOption"
							colSpan={colSpan}
							isLoading={loading}
							description="otoPackManualOption.description"
							direction="row-reverse">
							<SwitchInput className="mt-3" name="otoPackManualOption" />
						</FormItem>
					)}

					{isAuthorized('WMSModule') && (
						<>
							<F.Line className="line" />
							<FormItem
								label="otoPackDeactivatePicking"
								colSpan={colSpan}
								isLoading={loading}
								description="otoPackDeactivatePicking.description"
								direction="row-reverse">
								<SwitchInput className="mt-3" name="otoPackDeactivatePicking" disabled={activateBins} />
							</FormItem>
						</>
					)}

					{isAuthorized('WMSAdvanceModule') && (
						<>
							<F.Line />
							<FormItem
								label="activateBins"
								colSpan={colSpan}
								isLoading={loading}
								description="activateBins.description"
								direction="row-reverse">
								<SwitchInput className="mt-3" name="activateBins" />
							</FormItem>

							<F.Line className="line" />
							<FormItem
								label="otoPackCheckInPacking"
								colSpan={colSpan}
								isLoading={loading}
								description="otoPackCheckInPacking.description"
								direction="row-reverse">
								<SwitchInput
									className="mt-3"
									value={activateBins ? watch('otoPackActivatePackInvCheck') : false}
									setValue={(e) =>
										setValue('otoPackActivatePackInvCheck', e.target.checked, formOptions)
									}
									disabled={!activateBins}
									disabledTooltip={!activateBins && 'activateBinsDisabledSubItems'}
								/>
							</FormItem>
							<F.Line className="line" />
							<FormItem
								label="otoPackActivateWaveInvCheck"
								colSpan={colSpan}
								isLoading={loading}
								description="otoPackActivateWaveInvCheck.description"
								direction="row-reverse">
								<SwitchInput
									className="mt-3"
									value={activateBins ? watch('otoPackActivateWaveInvCheck') : false}
									setValue={(e) =>
										setValue('otoPackActivateWaveInvCheck', e.target.checked, formOptions)
									}
									disabled={!activateBins}
									disabledTooltip={!activateBins && 'activateBinsDisabledSubItems'}
								/>
							</FormItem>
						</>
					)}
					{isAuthorized('WMSModule', 'WMSAdvanceModule') && (
						<>
							<F.Line className="line" />
							<FormItem
								label="otoPackPackingPageAutomaticPrintAWB"
								colSpan={colSpan}
								isLoading={loading}
								description="otoPackPackingPageAutomaticPrintAWB.description"
								direction="row-reverse">
								<SwitchInput className="mt-3" name="otoPackPackingPageAutomaticPrintAWB" />
							</FormItem>
							<F.Line className="line" />
							<FormItem
								label="otoPackPackingPageAutomaticShipment"
								colSpan={colSpan}
								isLoading={loading}
								description="otoPackPackingPageAutomaticShipment.description"
								direction="row-reverse">
								<SwitchInput className="mt-3" name="otoPackPackingPageAutomaticShipment" />
							</FormItem>
						</>
					)}

					<F.Line className="line" />
					<FormItem
						label="activateBundleFeature"
						colSpan={colSpan}
						isLoading={loading}
						description="activateBundleFeature.description"
						direction="row-reverse">
						<SwitchInput className="mt-3" name="activateBundleFeature" />
					</FormItem>
					{isAuthorized('WMSAdvanceModule') && (
						<>
							<F.Line className="line" />
							<FormItem
								label="otoPackUpdateStatusAfterShipping"
								colSpan={colSpan}
								isLoading={loading}
								description="otoPackUpdateStatusAfterShipping.description"
								direction="row-reverse">
								<SwitchInput className="mt-3" name="otoPackUpdateStatusAfterShipping" />
							</FormItem>
						</>
					)}
				</S.StyledCard>
			</S.StyledForm>
			<F.FormFooter>
				<Button
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					icon={faFloppyDisk}
					label="save"
					variant="blue"
					className="saveButton"
				/>
			</F.FormFooter>
		</div>
	);
};
export default OTOPack;
